// The plug-in seam — FROZEN by F-SHELL (plan §2.1 / §8.1).
//
// Per-feature self-registration: each wave-1/2 tab feature owns a
// `src/ui/features/<f>/register.ts` that calls `registerTab({...})`. The barrel
// `src/ui/features/index.ts` imports each feature's register.ts as a side
// effect (ONE line per feature — the only shared edit point; disjoint appends
// auto-merge in git). The shell renders from `getTabs()`; it is NEVER edited
// after F-SHELL.
//
// Why a registry for 5 fixed tabs? The 5 panes are built by 5 PARALLEL feature
// sessions (MASTER §3). A single static array would force every session to edit
// one file → N-way collision. The registry is the load-bearing parallel-safety
// requirement, not over-engineering (plan C10).

import type { ComponentType, ReactNode } from 'react'

/**
 * FROZEN tab identity union — matches the 5 lawyer-surface tabs. Constraining
 * `TabDef.id` to this union means the registry can't grow an unknown tab: a
 * wave-2 feature beyond the 5 needs a deliberate `TabId` extension + a plan
 * amendment, not a silent insert (plan §2.1).
 */
export type TabId = 'sources' | 'agent' | 'plan' | 'comments' | 'versions'

/**
 * The contract every feature registers against (FROZEN — plan §8.1). `badge`
 * and `live` are SELECTOR functions (not values) so the shell re-reads them on
 * each render without the feature pushing into shell state — a feature wires
 * its own Zustand selector (e.g. `useCommentStore.getState().unread`) inside
 * `badge`.
 */
export interface TabDef {
  /** One of the 5 frozen ids. */
  id: TabId
  /** Explicit, deterministic ordering (FROZEN: 10/20/30/40/50 — gaps for inserts). */
  order: number
  /** i18n key, MUST resolve in fr/ar/en (e.g. 'shell.tab.sources'). Shipped by F-SHELL. */
  labelKey: string
  /** The tab glyph/icon (feature-owned; RTL-flip per CLAUDE.md §9.2). */
  icon: ReactNode
  /** The active-pane component (lazy-friendly). */
  Pane: ComponentType
  /** Optional count badge (e.g. comments) — a selector, read live. */
  badge?: () => number | null
  /** Optional live-pulse (e.g. agent streaming) — a selector, read live. */
  live?: () => boolean
}

/**
 * The 5 known tabs in FROZEN order (sources=10, agent=20, plan=30,
 * comments=40, versions=50 — plan §2.7, MASTER order, Plan 3rd). This is the
 * SHELL chrome contract: the TabBar always renders these 5 buttons (so the
 * shell is demoable with ZERO features registered — every tab's pane is then a
 * <ComingSoon>). A registered TabDef enriches its tab (real icon/badge/live +
 * a real Pane); an unregistered tab still gets its label + order from here.
 *
 * `labelKey` is shipped by F-SHELL in `shell.json × 3`; a feature's
 * `register.ts` reuses the SAME key (does not duplicate it). The 5-member set
 * is FROZEN: extending it needs a deliberate `TabId` + this list amendment.
 */
export const KNOWN_TABS: ReadonlyArray<{
  id: TabId
  order: number
  labelKey: string
}> = [
  { id: 'sources', order: 10, labelKey: 'shell.tab.sources' },
  { id: 'agent', order: 20, labelKey: 'shell.tab.agent' },
  { id: 'plan', order: 30, labelKey: 'shell.tab.plan' },
  { id: 'comments', order: 40, labelKey: 'shell.tab.comments' },
  { id: 'versions', order: 50, labelKey: 'shell.tab.versions' },
]

// Module-global registry. Populated synchronously at module-eval time when the
// feature barrel runs (main.tsx imports it before createRoot — no async race).
const registry = new Map<TabId, TabDef>()

/**
 * Register a feature's tab. Throws on duplicate id (no silent overwrite) so a
 * mis-wired double-registration fails loudly at the offending feature's CI
 * rather than silently shadowing another feature's tab.
 */
export function registerTab(def: TabDef): void {
  if (registry.has(def.id)) {
    throw new Error(`[tab-registry] duplicate tab id "${def.id}"`)
  }
  registry.set(def.id, def)
}

/** All registered tabs, sorted by `order` ascending. */
export function getTabs(): TabDef[] {
  return [...registry.values()].sort((a, b) => a.order - b.order)
}

/** Look up a single registered tab, or `undefined` if not yet registered. */
export function getTab(id: TabId): TabDef | undefined {
  return registry.get(id)
}

/**
 * Test seam — clears the module-global registry between test files (the
 * registry leaks across files otherwise). Call in `beforeEach`. NOT for
 * production use.
 */
export function __resetRegistry(): void {
  registry.clear()
}
